import { defaults, outputKeys } from './graph';
import { waitDateFields, waitSources } from './nodeSettings';
import { validSchedule } from './startSchedule';

export const makeEdge = (source, sourceHandle, target) => ({
  id: `${source}:${sourceHandle}`,
  source,
  sourceHandle,
  target,
  type: 'editable',
});

export const isInlineEnd = node =>
  node?.data.kind === 'exit' && node.data.editor_terminal === true;

export const pruneInlineEnds = (nodes, edges) =>
  nodes.filter(
    node => !isInlineEnd(node) || edges.some(edge => edge.target === node.id)
  );

// Explicit completion compiles to a regular v2 exit, so existing executors need no new semantics.
export const finishBranch = (nodes, edges, source, output, id) => {
  const parent = nodes.find(node => node.id === source);
  if (!parent || !outputKeys(parent.data.kind).includes(output))
    throw new Error('invalidConnection');
  let terminal = nodes.find(isInlineEnd);
  if (!terminal && nodes.length >= 101) throw new Error('nodeLimit');
  terminal ||= {
    id,
    type: 'mres3',
    hidden: true,
    position: { x: 0, y: 0 },
    data: { kind: 'exit', editor_terminal: true },
  };
  return {
    nodes: nodes.includes(terminal) ? nodes : [...nodes, terminal],
    edges: [
      ...edges.filter(
        edge => !(edge.source === source && edge.sourceHandle === output)
      ),
      makeEdge(source, output, terminal.id),
    ],
  };
};

// Rank a DAG left-to-right, then centre each column. Cycles remain untouched for validation.
export const arrangeGraph = (nodes, edges) => {
  const visible = nodes.filter(node => !isInlineEnd(node));
  const ids = new Set(visible.map(node => node.id));
  const links = edges.filter(
    edge => ids.has(edge.source) && ids.has(edge.target)
  );
  const indegree = Object.fromEntries(
    visible.map(node => [
      node.id,
      links.filter(edge => edge.target === node.id).length,
    ])
  );
  const ranks = Object.fromEntries(visible.map(node => [node.id, 0]));
  const queue = visible.filter(node => !indegree[node.id]).map(node => node.id);
  const ordered = [];
  while (queue.length) {
    const id = queue.shift();
    ordered.push(id);
    links
      .filter(edge => edge.source === id)
      .forEach(edge => {
        ranks[edge.target] = Math.max(ranks[edge.target], ranks[id] + 1);
        indegree[edge.target] -= 1;
        if (!indegree[edge.target]) queue.push(edge.target);
      });
  }
  if (ordered.length !== visible.length) return nodes;
  const columns = [];
  ordered.forEach(id => {
    columns[ranks[id]] ||= [];
    columns[ranks[id]].push(visible.find(node => node.id === id));
  });
  const height = node => 180 + outputKeys(node.data.kind).length * 32;
  const columnHeight = column =>
    column.reduce((sum, node) => sum + height(node) + 56, 0);
  const totalHeight = Math.max(...columns.map(columnHeight));
  const positions = {};
  columns.forEach((column, rank) => {
    let y = 40 + (totalHeight - columnHeight(column)) / 2;
    column.forEach(node => {
      positions[node.id] = { x: 40 + rank * 360, y };
      y += height(node) + 56;
    });
  });
  return nodes.map(node =>
    positions[node.id] ? { ...node, position: positions[node.id] } : node
  );
};

export const connectionError = (nodes, edges, connection) => {
  const source = nodes.find(node => node.id === connection.source);
  const target = nodes.find(node => node.id === connection.target);
  if (!source || !target || target.id === 'trigger') return 'invalidConnection';
  if (source.id === target.id) return 'selfConnection';
  if (!outputKeys(source.data.kind).includes(connection.sourceHandle || 'next'))
    return 'invalidConnection';
  const visited = new Set();
  const pending = [target.id];
  while (pending.length) {
    const id = pending.pop();
    if (id === source.id) return 'cycleConnection';
    if (!visited.has(id)) {
      visited.add(id);
      edges
        .filter(edge => edge.source === id)
        .forEach(edge => pending.push(edge.target));
    }
  }
  return null;
};

// The caller chooses a continuation for a branching step; never guess an outcome.
export const insertStep = ({
  nodes,
  edges,
  source,
  output,
  kind,
  continuation,
  timezone,
  id,
}) => {
  const parent = nodes.find(node => node.id === source);
  if (!parent || !outputKeys(parent.data.kind).includes(output))
    throw new Error('invalidConnection');
  if (nodes.length >= 101) throw new Error('nodeLimit');
  const existing = edges.find(
    edge => edge.source === source && edge.sourceHandle === output
  );
  if (existing && !outputKeys(kind).includes(continuation))
    throw new Error('continuationRequired');
  const position = {
    x: parent.position.x + 360,
    y: parent.position.y + outputKeys(parent.data.kind).indexOf(output) * 80,
  };
  const height = 180 + outputKeys(kind).length * 44;
  while (
    nodes.some(
      node =>
        !isInlineEnd(node) &&
        Math.abs(node.position.x - position.x) < 280 &&
        position.y <
          node.position.y + 180 + outputKeys(node.data.kind).length * 44 &&
        position.y + height > node.position.y
    )
  )
    position.y += 100;
  const nextEdges = edges.filter(edge => edge !== existing);
  nextEdges.push(makeEdge(source, output, id));
  if (existing) nextEdges.push(makeEdge(id, continuation, existing.target));
  return {
    nodes: [
      ...nodes,
      {
        id,
        type: 'mres3',
        position,
        data: { kind, ...defaults(kind, timezone) },
      },
    ],
    edges: nextEdges,
  };
};

// Fast editing feedback. The server remains authoritative for preview/publication.
export const editorIssues = (nodes, edges, catalog, templates) => {
  const issues = [];
  const add = (node, code, output) => issues.push({ node, code, output });
  const reached = new Set();
  const pending = ['trigger'];
  while (pending.length) {
    const id = pending.pop();
    if (!reached.has(id)) {
      reached.add(id);
      edges
        .filter(edge => edge.source === id)
        .forEach(edge => pending.push(edge.target));
    }
  }
  const trigger = nodes.find(node => node.id === 'trigger')?.data || {};
  const event = trigger.mode === 'schedule' ? 'scheduled' : trigger.event;
  nodes.forEach(({ id, data }) => {
    if (!reached.has(id)) add(id, 'unreachable_node');
    outputKeys(data.kind).forEach(output => {
      if (
        !edges.some(edge => edge.source === id && edge.sourceHandle === output)
      )
        add(id, 'openOutput', output);
    });
    if (data.kind === 'trigger') {
      if (
        !(catalog.connections || []).some(
          item => item.id === data.connection_id && item.active
        )
      )
        add(id, 'connection_required');
      if (data.mode === 'schedule' && !validSchedule(data.schedule))
        add(id, 'invalid_schedule');
      if (
        data.mode !== 'schedule' &&
        !(catalog.events || []).includes(data.event)
      )
        add(id, 'invalid_trigger');
      if (
        data.mode !== 'schedule' &&
        data.event === 'response' &&
        !(catalog.response_actions || []).includes(data.response_action)
      )
        add(id, 'response_action_required');
    }
    if (data.kind === 'condition') {
      const context = data.definition?.context || 'appointment';
      if (trigger.entity === 'patients' && context !== 'patient')
        add(id, 'appointment_context_unavailable');
      if (
        (data.definition?.rules || []).some(
          rule => rule.op === 'changed' && event !== 'updated'
        )
      )
        add(id, 'changed_requires_update');
      if (!['appointment', 'patient', 'appointment_service'].includes(context))
        add(id, 'condition_context_invalid');
      if (
        (data.definition?.rules || []).some(
          rule =>
            rule.source === 'previous' &&
            !['updated', 'deleted'].includes(event)
        )
      )
        add(id, 'previous_context_unavailable');
      if (
        (data.definition?.rules || []).some(rule =>
          context === 'appointment'
            ? !['appointment', 'event', 'previous', 'computed'].includes(
                rule.source
              )
            : rule.source !== context
        )
      )
        add(id, 'invalid_rule');
      if (!data.definition?.rules?.length) add(id, 'rules_required');
      else if (
        data.definition.rules.some(
          rule =>
            !rule.field ||
            !rule.source ||
            !rule.op ||
            (!['present', 'blank', 'changed', 'today'].includes(rule.op) &&
              (rule.value === null ||
                rule.value === undefined ||
                rule.value === ''))
        )
      )
        add(id, 'invalid_rule');
    }
    if (['message', 'availability'].includes(data.kind)) {
      if (
        trigger.entity === 'patients' &&
        (data.source_mode === 'appointment' || !data.inbox_id)
      )
        add(id, 'patient_source_required');
      if (
        !data.channel ||
        (data.source_mode !== 'appointment' &&
          !(catalog.inboxes || []).some(
            item => item.id === data.inbox_id && item.channel === data.channel
          ))
      )
        add(id, 'inbox_required');
    }
    if (['message', 'action'].includes(data.kind)) {
      const template = templates.find(item => item.id === data.template_id);
      if (!template) add(id, 'template_required');
      else if (
        data.kind === 'message' &&
        data.channel &&
        !(
          template.channel_variants?.[data.channel]?.body ||
          (template.channel === data.channel && template.body)
        )
      )
        add(id, 'template_channel_missing');
    }
    if (data.kind === 'message' && !(Number(data.timeout_minutes) > 0))
      add(id, 'result_timeout_required');
    if (data.kind === 'wait') {
      if (!data.timezone) add(id, 'timezone_required');
      if (data.mode === 'window') {
        if (
          !data.window_from ||
          !data.window_to ||
          data.window_from === data.window_to
        )
          add(id, 'invalid_window');
      } else {
        if (
          data.amount === null ||
          data.amount === undefined ||
          data.amount === '' ||
          Number(data.amount) < 0 ||
          !data.unit
        )
          add(id, 'duration_required');
        if (data.mode === 'dynamic') {
          if (trigger.entity === 'patients')
            add(id, 'appointment_context_unavailable');
          if (
            !waitDateFields(catalog).some(field => field.name === data.field) ||
            !['appointment', 'event', 'previous'].includes(data.source)
          )
            add(id, 'date_field_required');
          if (
            data.source === 'previous' &&
            !waitSources(event).includes('previous')
          )
            add(id, 'previous_context_unavailable');
        }
        if (data.mode === 'dynamic' && !data.date_format)
          add(id, 'date_format_required');
      }
    }
  });
  edges.forEach(edge => {
    const code = connectionError(nodes, edges, edge);
    if (code) add(edge.source, code);
  });
  return issues;
};
